import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { SmoothScroll } from "@/components/SmoothScroll";
import { SEO } from "@/components/SEO";
import { Nav } from "@/components/Nav";
import { Hero } from "@/components/Hero";
import { SearchFilterBar } from "@/components/SearchFilterBar";
import { FeaturedEvents } from "@/components/FeaturedEvents";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { Manifesto } from "@/components/Manifesto";
import { Lifestyle } from "@/components/Lifestyle";
import { Testimonials } from "@/components/Testimonials";
import { Newsletter } from "@/components/Newsletter";
import { ClosingManifesto } from "@/components/ClosingManifesto";
import { Footer } from "@/components/Footer";
import { EventDetailPage } from "@/components/EventDetailPage";
import { AccessGate } from "@/components/AccessGate";
import { ScrollProgress } from "@/components/ScrollProgress";

// Reset scroll to top on route change (Lenis-aware). If the URL contains a
// hash, we skip the top reset so the hash-scroll effect in <Landing/> can
// take the user directly to that section (route-aware nav from a sub-page).
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (hash) return;
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true, force: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
};

const landingJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "BlackTieDo",
  url: "https://blacktiedo.co.uk",
  description:
    "The private portal to Britain's charity balls, business awards and Highland balls.",
  sameAs: [],
};

const Landing = () => {
  const [eventType, setEventType] = useState("all");
  const [month, setMonth] = useState("any");
  const { hash } = useLocation();

  // If the landing was navigated to with a hash (e.g. "/#events-v2" from a
  // sub-page's nav link), scroll to that section once it has mounted.
  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      if (window.__lenis) {
        window.__lenis.scrollTo(el, { offset: -80 });
      } else {
        const y = el.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 500);
    return () => clearTimeout(t);
  }, [hash]);

  const handleSearch = () => {
    if (typeof window !== "undefined") {
      const el = document.querySelector("#events");
      if (window.__lenis && el) {
        window.__lenis.scrollTo(el, { offset: -80 });
      } else if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <div
      data-testid="landing-page"
      className="relative bg-[color:var(--bt-bg)] text-[color:var(--bt-ink)] min-h-screen overflow-x-hidden"
    >
      <SEO
        title=""
        description="The private portal to Britain's charity balls, business awards and Highland balls - quietly curated, and rarely written about."
        path="/"
        jsonLd={landingJsonLd}
      />
      <div className="bt-grain" aria-hidden />
      <ScrollProgress />
      <Nav />
      <main>
        <Hero />
        <SearchFilterBar
          eventType={eventType}
          setEventType={setEventType}
          month={month}
          setMonth={setMonth}
          onSearch={handleSearch}
        />
        <FeaturedEvents
          eventType={eventType}
          month={month}
          setEventType={setEventType}
        />
        <EditorialMarquee />
        <Manifesto />
        <Lifestyle />
        <Testimonials />
        <Newsletter />
        <ClosingManifesto />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <AccessGate>
        <BrowserRouter>
          <SmoothScroll>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/event/:id" element={<EventDetailPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </SmoothScroll>
        </BrowserRouter>
      </AccessGate>
    </HelmetProvider>
  );
}

export default App;
